import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  description, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  type = 'danger',
  isLoading = false
}) => {
  if (!isOpen) return null;

  const colors = {
    danger: {
      bg: 'bg-red-50',
      text: 'text-red-600',
      button: 'bg-red-600 hover:bg-red-700 shadow-red-100',
    },
    warning: {
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700 shadow-amber-100',
    },
    info: {
      bg: 'bg-brand-indigo/10',
      text: 'text-brand-indigo',
      button: 'bg-brand-indigo hover:bg-brand-indigo/90 shadow-brand-indigo/10',
    }
  };

  const style = colors[type] || colors.danger;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        
        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl relative overflow-hidden z-10"
        >
          <div className={`absolute top-0 right-0 w-32 h-32 ${style.bg} rounded-full -mr-16 -mt-16 opacity-50`}></div>
          
          <div className="relative z-10">
            <div className={`w-16 h-16 ${style.bg} ${style.text} rounded-2xl flex items-center justify-center mb-6`}>
              <AlertTriangle size={32} />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
            <p className="text-gray-500 font-medium mb-8 leading-relaxed">
              {description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={onConfirm}
                disabled={isLoading}
                className={`flex-1 text-white font-bold py-4 rounded-2xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 ${style.button}`}
              >
                {isLoading ? 'Processing...' : confirmText}
              </button>
              <button 
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 bg-gray-50 text-gray-500 font-bold py-4 rounded-2xl hover:bg-gray-100 transition-all active:scale-95"
              >
                {cancelText}
              </button>
            </div>
          </div>
          
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-900 transition-colors z-20"
          >
            <X size={20} />
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfirmModal;
